import { numberFormat } from '../../../helpers/utils';
import { HalfPageModule, SectionTitle } from '../../../styles/global.styles';
import { FormattedMessage } from 'react-intl';
import {
  AfricaValue,
  AmericaValue,
  AsiaValue,
  CustomLegendContainer,
  EuropeValue,
  NationalitiesContainer,
  NationalityContainer,
  NationalityTag,
  NationalityValue,
  NationalValue,
  OceaniaValue,
  SmallGraphContainer,
  SmallGraphInfoContainer,
  SmallGraphSuffix,
  SmallGraphValue,
  SubsectionTitle,
  ZonePopulationContainer,
} from './zonePopulation.styles';
import ZonePopulationChart from './zonePopulationGraph/zonePopulationNationalityGraph.component';
import { AgeDistributionGraph } from './zonePopulationMeanAgeGraph/meanAgeGraph.component';
import { StudyLevelGraph } from './zonePopulationStudyLevelGraph/studyLevelGraph.component';
import {
  MeanAgeModel,
  NationalityModel,
  StudyLevelModel,
  ZonePopulationDataModel,
} from '../../../types/indicators.interfaces';
import { NoDataContainer } from '../NoDataComponent/noData.styles';
import NoDataIcon from '../../../assets/images/error.png';

// Check if nationality data has valid values
const hasNationalityData = (nationality: NationalityModel): boolean => {
  return Object.values(nationality).some(
    (value) => value !== null && value !== undefined && Number(value) >= 1
  );
};

// Check if mean age data has valid values
const hasMeanAgeData = (meanAge: MeanAgeModel): boolean => {
  if (meanAge.value === null || meanAge.value === undefined) {
    return false;
  }
  return (
    meanAge.ranges?.some(
      (range) => range.value !== null && Number(range.value) > 0
    ) ?? false
  );
};

// Check if study level data has valid values
const hasStudyLevelData = (studyLevel: StudyLevelModel): boolean => {
  if (studyLevel.value === null || studyLevel.value === undefined) {
    return false;
  }
  return Object.values(studyLevel.categories ?? {}).some(
    (value) => value !== null && value !== undefined && Number(value) > 0
  );
};

export const ZonePopulation = (props: ZonePopulationDataModel) => {
  const nationalities = [
    {
      region: 'zonePopulation.nationalityNational',
      value: props.nationality.national,
      Component: NationalValue,
    },
    {
      region: 'zonePopulation.nationalityEurope',
      value: props.nationality.europe,
      Component: EuropeValue,
    },
    {
      region: 'zonePopulation.nationalityAsia',
      value: props.nationality.asia,
      Component: AsiaValue,
    },
    {
      region: 'zonePopulation.nationalityAfrica',
      value: props.nationality.africa,
      Component: AfricaValue,
    },
    {
      region: 'zonePopulation.nationalityOceania',
      value: props.nationality.oceania,
      Component: OceaniaValue,
    },
    {
      region: 'zonePopulation.nationalityAmerica',
      value: props.nationality.america,
      Component: AmericaValue,
    },
  ];

  const sortedNationalities = nationalities.sort(
    (a, b) => (Number(b.value) || 0) - (Number(a.value) || 0)
  );

  const hasNationality = hasNationalityData(props.nationality);
  const hasMeanAge = hasMeanAgeData(props.mean_age);
  const hasStudyLevel = hasStudyLevelData(props.study_level);

  return (
    <HalfPageModule>
      <SectionTitle>
        <FormattedMessage id="zonePopulation.title" />
      </SectionTitle>
      <ZonePopulationContainer>
        <NationalityContainer>
          {hasNationality ? (
            <>
              <CustomLegendContainer>
                <SubsectionTitle>
                  <FormattedMessage id="zonePopulation.nationality" />
                </SubsectionTitle>
                <NationalitiesContainer>
                  {sortedNationalities.map((nationality, index) => {
                    const Component = nationality.Component;
                    if (!nationality.value || Number(nationality.value) === 0) {
                      return null;
                    }
                    return (
                      <Component key={index}>
                        <NationalityValue>
                          {!isNaN(Number(nationality.value)) &&
                          Number(nationality.value.toFixed(1)) > 0
                            ? `${numberFormat.format(
                                Number(nationality.value.toFixed(1))
                              )}%`
                            : '-%'}
                        </NationalityValue>
                        <NationalityTag>
                          <FormattedMessage id={nationality.region} />
                        </NationalityTag>
                      </Component>
                    );
                  })}
                </NationalitiesContainer>
              </CustomLegendContainer>
              <ZonePopulationChart {...(props.nationality as NationalityModel)} />
            </>
          ) : (
            <NoDataContainer>
              <img src={NoDataIcon} />
              Not enough data to build the graph.
            </NoDataContainer>
          )}
        </NationalityContainer>
        <SmallGraphContainer>
          <SubsectionTitle>
            <FormattedMessage id="zonePopulation.meanAge" />
          </SubsectionTitle>
          {hasMeanAge ? (
            <>
              <SmallGraphInfoContainer>
                <SmallGraphValue>
                  {Math.round(Number(props.mean_age.value ?? 0))}
                </SmallGraphValue>
                <SmallGraphSuffix>
                  <FormattedMessage id="zonePopulation.years" />
                </SmallGraphSuffix>
              </SmallGraphInfoContainer>
              <AgeDistributionGraph {...(props.mean_age as MeanAgeModel)} />
            </>
          ) : (
            <NoDataContainer>
              <img src={NoDataIcon} />
              Not enough data to build the graph.
            </NoDataContainer>
          )}
        </SmallGraphContainer>
        <SmallGraphContainer>
          <SubsectionTitle>
            <FormattedMessage id="zonePopulation.studyLevel" />
          </SubsectionTitle>
          {hasStudyLevel ? (
            <>
              <SmallGraphInfoContainer>
                <SmallGraphValue>
                  {numberFormat.format(Number(props.study_level.value ?? 0))}%
                </SmallGraphValue>
                <SmallGraphSuffix>
                  <FormattedMessage id="zonePopulation.college" />
                </SmallGraphSuffix>
              </SmallGraphInfoContainer>
              <StudyLevelGraph {...(props.study_level as StudyLevelModel)} />
            </>
          ) : (
            <NoDataContainer>
              <img src={NoDataIcon} />
              Not enough data to build the graph.
            </NoDataContainer>
          )}
        </SmallGraphContainer>
      </ZonePopulationContainer>
    </HalfPageModule>
  );
};
